import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

function SafetyRules({ user, onLogout }) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  
  // Admin-only states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    fetchBooks();
    fetchHistory();
  }, []);

  const fetchBooks = async () => {
    try {
      // Only fetch safety books for this page
      const response = await axios.get(`${API}/books/list?category=safety`);
      setBooks(response.data);
    } catch (err) {
      console.error('Error fetching books:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/qa/history`);
      setHistory(response.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const handleDownloadBook = (bookId, filename) => {
    const token = localStorage.getItem('token');
    fetch(`${API}/book/download/${bookId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(err => alert('Failed to download book'));
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this safety rule document?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/delete-book/${bookId}`);
      setBooks(books.filter(b => b.id !== bookId));
      alert('Document deleted successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setUploadError('');
      setUploadSuccess(false);
    } else {
      setUploadError('Please select a valid PDF file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadError('');
    setUploadSuccess(false);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', 'safety');  // Mark as safety document

      await axios.post(`${API}/admin/upload-book`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadSuccess(true);
      setSelectedFile(null);
      fetchBooks();
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/qa/ask`, {
        question: question.trim(),
        category: "safety"  // Safety page uses safety category
      });

      setHistory([response.data, ...history]);
      setQuestion('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="dashboard" data-testid="safety-rules-page">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img 
            src="/stewie_front.png"
            alt="Stewie Logo"
            className="dashboard-logo-image"
          />
          Ask Stewie
        </div>
        <div className="dashboard-user">
          <button
            className="btn-nav"
            onClick={() => navigate('/ai-assistant')}
            data-testid="ai-assistant-nav"
          >
            🤖 AI Assistant
          </button>
          <button
            className="btn-nav"
            onClick={() => navigate('/user-guide')}
            data-testid="user-guide-nav"
          >
            📖 Help
          </button>
          <div className="user-info">
            <div className="user-name" data-testid="user-name">{user.name}</div>
            <div className="user-role">{user.is_admin ? 'Administrator' : 'User'}</div>
          </div>
          <button className="btn-logout" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Admin Upload Section - Only visible to admins */}
        {user.is_admin && (
          <div className="upload-section" data-testid="admin-upload-section">
            <h2 className="upload-header">
              <span>📤</span>
              Upload Safety Rules Document
            </h2>

            {uploadError && <div className="error-message" data-testid="upload-error">{uploadError}</div>}
            {uploadSuccess && (
              <div className="success-message" data-testid="upload-success">
                Document uploaded successfully!
              </div>
            )}

            <div
              className="upload-area"
              onClick={() => document.getElementById('safety-file-input').click()}
              data-testid="upload-area"
            >
              <div className="upload-icon">📄</div>
              <div className="upload-text">Click to select a PDF file</div>
              <div className="upload-hint">Only PDF files are supported</div>
            </div>

            <input
              type="file"
              id="safety-file-input"
              className="file-input"
              accept=".pdf"
              onChange={handleFileSelect}
              data-testid="file-input"
            />

            {selectedFile && (
              <div className="selected-file" data-testid="selected-file">
                <span className="file-name">
                  <span>📎</span>
                  {selectedFile.name}
                </span>
                <button
                  className="btn-upload"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="upload-button"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Safety Rules Library */}
        {books.length > 0 ? (
          <div className="books-library-section" data-testid="safety-books-library">
            <h2 className="section-title">
              <span>🦺</span>
              Safety Rules for Life ({books.length})
            </h2>
            <div className="books-grid">
              {books.map((book) => (
                <div key={book.id} className="book-card" data-testid={`book-card-${book.id}`}>
                  <div 
                    className="book-card-image"
                    onClick={() => handleDownloadBook(book.id, book.filename)}
                    data-testid={`download-book-${book.id}`}
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_book-whisperer-1/artifacts/5ardfsrk_image.jpeg"
                      alt="Union Logo"
                      className="book-thumbnail"
                    />
                    <div className="download-overlay">
                      <span className="download-icon">📥</span>
                      <span className="download-text">Download</span>
                    </div>
                  </div>
                  <div className="book-card-info">
                    <h3 className="book-card-title" data-testid="book-title">{book.title}</h3>
                    <div className="book-card-meta">
                      {formatDate(book.upload_date)}
                    </div>
                    <div className="book-card-meta">
                      {book.chunk_count} sections
                    </div>
                    {user.is_admin && (
                      <button
                        className="btn-delete-book"
                        onClick={() => handleDeleteBook(book.id)}
                        data-testid={`delete-book-${book.id}`}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="no-book-message" data-testid="no-book-message">
            <div className="no-book-icon">🦺</div>
            <p>No safety rules documents have been uploaded yet.</p>
            {!user.is_admin && <p>Please contact your administrator.</p>}
          </div>
        )}

        {/* Ask Stewie Section */}
        {books.length > 0 && (
          <div className="qa-section">
            <h2 className="qa-header">
              <img 
                src="/stewie_front.png"
                alt="Stewie"
                className="stewie-icon"
              />
              Ask Stewie
            </h2>

            <div className="qa-input-container">
              {error && <div className="error-message" data-testid="qa-error">{error}</div>}
              <form onSubmit={handleAskQuestion}>
                <div className="qa-input-wrapper">
                  <input
                    type="text"
                    className="qa-input"
                    placeholder="Ask about safety rules..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    disabled={loading}
                    data-testid="question-input"
                  />
                  <button
                    type="submit"
                    className="btn-ask-stewie"
                    disabled={loading || !question.trim()}
                    data-testid="ask-button"
                  >
                    <img 
                      src="/stewie_front.png"
                      alt="Stewie"
                      className="button-stewie-icon"
                    />
                    {loading ? 'Asking...' : 'Ask Stewie'}
                  </button>
                </div>
              </form>
            </div>

            <div className="qa-history" data-testid="qa-history">
              {history.length > 0 ? (
                history.map((item, index) => (
                  <div key={index} className="qa-item" data-testid={`qa-item-${index}`}>
                    <div className="qa-question">
                      <span>❓</span>
                      {item.question}
                    </div>
                    <div className="qa-answer" data-testid={`qa-answer-${index}`}>{item.answer}</div>
                    <div className="qa-timestamp">{formatDate(item.timestamp)}</div>
                  </div>
                ))
              ) : (
                <div className="empty-history" data-testid="empty-history">
                  <p>No questions asked yet. Start by asking about safety rules!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SafetyRules;
